import React, { useEffect, useRef, useState } from 'react';
import { X, Send, Loader2, MessageSquare } from 'lucide-react';
import * as chatApi from '../../../api/chat';
import type { Message } from '../../../api/chat';
import MessageBubble from './MessageBubble';
import DateDivider from './DateDivider';
import { groupMessages, type PendingMessage } from '../utils/format';

type Props = {
  conversationId: string;
  parent: Message;
  currentUserId?: string;
  currentUserName?: string;
  replies: PendingMessage[];
  setReplies: React.Dispatch<React.SetStateAction<PendingMessage[]>>;
  onClose: () => void;
  wsSend: (data: object) => void;
};

function ThreadPanel({
  conversationId,
  parent,
  currentUserId,
  currentUserName,
  replies,
  setReplies,
  onClose,
  wsSend,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);
  const scrollEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Load replies on mount / when parent changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    chatApi.getThread(conversationId, parent.id)
      .then(list => {
        if (!cancelled) setReplies(list);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load thread');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [conversationId, parent.id, setReplies]);

  // Auto-scroll to bottom when replies change
  useEffect(() => {
    scrollEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [replies.length]);

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    setSendError(null);

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    const tempId = `thread-temp-${Date.now()}`;
    const optimistic: PendingMessage = {
      id: tempId,
      conversation_id: conversationId,
      sender_id: currentUserId || '',
      sender_name: currentUserName || 'You',
      content: text,
      message_type: 'text',
      thread_parent_id: parent.id,
      reply_to_id: null,
      is_edited: false,
      is_deleted: false,
      created_at: new Date().toISOString(),
      reactions: [],
      attachments: [],
      _pending: true,
    };

    setReplies(prev => [...prev, optimistic]);

    try {
      wsSend({
        type: 'message',
        conversation_id: conversationId,
        content: text,
        message_type: 'text',
        thread_parent_id: parent.id,
      });
    } catch (e: any) {
      setReplies(prev => prev.filter(m => m.id !== tempId));
      setSendError('Failed to send reply');
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  const noop = () => {};
  const noopMsg = (_m: Message) => {};
  const noopId = (_id: string) => {};
  const noopReact = (_id: string, _e: string) => {};

  return (
    <div className="fixed inset-0 z-40 md:static md:inset-auto md:z-auto w-full md:w-[420px] md:flex-shrink-0 border-l border-gray-200 bg-white flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 bg-white border-b border-gray-200 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <MessageSquare size={16} className="text-blue-600 flex-shrink-0" />
          <h3 className="font-semibold text-gray-900 text-sm leading-tight truncate">Thread</h3>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          title="Close thread">
          <X size={16} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto py-3 bg-gray-50">
        {/* Parent message */}
        <MessageBubble
          msg={parent as PendingMessage}
          isMine={parent.sender_id === currentUserId}
          onReact={noopReact}
          onEdit={noopMsg}
          onDelete={noopId}
          onTask={noopMsg}
          currentUserName={currentUserName}
        />

        {/* Divider */}
        <div className="flex items-center gap-3 my-3 px-4">
          <div className="flex-1 h-px bg-black/10" />
          <span className="text-[11px] font-semibold text-gray-500">
            {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
          </span>
          <div className="flex-1 h-px bg-black/10" />
        </div>

        {/* Loading / error */}
        {loading && (
          <div className="flex justify-center py-4">
            <Loader2 size={16} className="animate-spin text-gray-400" />
          </div>
        )}
        {error && !loading && (
          <div className="mx-4 mb-3 p-3 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg">
            {error}
          </div>
        )}

        {/* Reply list (grouped) */}
        {!loading && groupMessages(replies).map(item => (
          item.kind === 'date' ? (
            <DateDivider key={item.key} label={item.label} />
          ) : (
            <MessageBubble
              key={item.msg.id}
              msg={item.msg}
              isMine={item.msg.sender_id === currentUserId}
              isFirstInGroup={item.isFirstInGroup}
              isLastInGroup={item.isLastInGroup}
              onReact={noopReact}
              onEdit={noopMsg}
              onDelete={noopId}
              onTask={noopMsg}
              currentUserName={currentUserName}
            />
          )
        ))}

        <div ref={scrollEndRef} className="h-2" />
      </div>

      {/* Send error */}
      {sendError && (
        <div className="px-4 py-2 text-xs text-red-600 bg-red-50 border-t border-red-100 flex items-center justify-between flex-shrink-0">
          <span>{sendError}</span>
          <button onClick={() => setSendError(null)} className="text-red-400 hover:text-red-600">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Composer */}
      <div className="px-4 py-3 bg-[#f0f2f5] border-t border-gray-200 flex-shrink-0">
        <div className="flex items-end gap-2">
          <div className="flex-1 bg-white rounded-2xl border border-gray-200 flex items-end shadow-sm overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition">
            <textarea
              ref={textareaRef}
              className="flex-1 px-4 py-2.5 text-sm resize-none focus:outline-none bg-transparent min-h-[40px] max-h-[120px]"
              placeholder="Reply in thread…"
              rows={1}
              value={input}
              onChange={handleTextareaChange}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }} />
          </div>
          <button
            onClick={handleSend}
            disabled={!input.trim()}
            className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 disabled:opacity-40 transition-all flex-shrink-0 shadow-sm hover:shadow-md active:scale-95">
            <Send size={16} className="translate-x-0.5" />
          </button>
        </div>
        <p className="text-[10px] text-gray-400 text-center mt-1.5">
          Enter to send · Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}

export default ThreadPanel;
